import { FC, memo, useCallback, useEffect, useRef, useState } from 'react';

import type { MeetChatMessage } from '../../../features/meet/chat/types';

import { Pressable } from '../../../shared/ui';

import { MeetChatImageLightbox } from './meet-chat-image-lightbox';

interface MeetChatMessageListProps {
  messages: MeetChatMessage[];
  emptyLabel?: string;
}

const timeFormatter = new Intl.DateTimeFormat('ru-RU', {
  hour: '2-digit',
  minute: '2-digit',
});

const formatMessageTime = (sentAt: string) => {
  const date = new Date(sentAt);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return timeFormatter.format(date);
};

interface ChatImagePreviewProps {
  src: string;
  onOpen: () => void;
}

const ChatImagePreview: FC<ChatImagePreviewProps> = ({ src, onOpen }) => (
  <Pressable
    onClick={onOpen}
    className="block w-full overflow-hidden rounded-control bg-surface-muted transition-opacity hover:opacity-90"
    aria-label="Открыть изображение на весь экран"
  >
    <img
      src={src}
      alt=""
      className="max-h-[min(40vh,180px)] w-full object-contain sm:max-h-[220px] md:max-h-[260px] lg:max-h-[280px]"
    />
  </Pressable>
);

const SCROLL_BOTTOM_THRESHOLD_PX = 48;

const isNearBottom = (container: HTMLDivElement) =>
  container.scrollHeight - container.scrollTop - container.clientHeight <= SCROLL_BOTTOM_THRESHOLD_PX;

const MeetChatMessageListComponent: FC<MeetChatMessageListProps> = ({
  messages,
  emptyLabel = 'Чат пока пуст. Напишите первое сообщение.',
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const shouldStickToBottomRef = useRef(true);
  const lastMessageIdRef = useRef<string | null>(null);
  const [fullscreenImageSrc, setFullscreenImageSrc] = useState<string | null>(null);

  useEffect(() => {
    const container = scrollContainerRef.current;
    const lastMessageId = messages.at(-1)?.id ?? null;

    if (!container || messages.length === 0) {
      if (messages.length === 0) {
        shouldStickToBottomRef.current = true;
        lastMessageIdRef.current = null;
      }

      return;
    }

    const hasNewMessage = lastMessageIdRef.current !== lastMessageId;
    lastMessageIdRef.current = lastMessageId;

    if (!hasNewMessage) {
      return;
    }

    if (!shouldStickToBottomRef.current && !isNearBottom(container)) {
      return;
    }

    requestAnimationFrame(() => {
      const scrollContainer = scrollContainerRef.current;

      if (!scrollContainer) {
        return;
      }

      scrollContainer.scrollTop = scrollContainer.scrollHeight;
      shouldStickToBottomRef.current = true;
    });
  }, [messages]);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;

    if (!container) {
      return;
    }

    shouldStickToBottomRef.current = isNearBottom(container);
  }, []);

  const renderMessageBody = (message: MeetChatMessage) => {
    if (message.kind === 'image' && message.image) {
      return (
        <ChatImagePreview src={message.image.dataUrl} onOpen={() => setFullscreenImageSrc(message.image!.dataUrl)} />
      );
    }

    return <p className="whitespace-pre-wrap break-words text-ui leading-5">{message.text}</p>;
  };

  return (
    <>
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 touch-pan-y overflow-y-auto overscroll-y-contain space-y-3 bg-surface-muted px-3 py-3 sm:px-4"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {messages.length === 0 ? (
          <div className="flex h-full min-h-[120px] items-center justify-center px-2 text-center text-ui text-ink-subtle sm:min-h-[180px]">
            {emptyLabel}
          </div>
        ) : (
          messages.map((message) => (
            <div key={message.id} className={`flex ${message.isOwn ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[min(100%,90%)] rounded-card px-3 py-2 shadow-control sm:max-w-[85%] ${
                  message.isOwn ? 'bg-accent text-ink' : 'bg-white text-ink'
                }`}
              >
                <div className="mb-1 flex min-w-0 items-center gap-2 text-ui-xs">
                  <span className="truncate font-medium">{message.authorName}</span>
                  <span className="shrink-0 opacity-70">{formatMessageTime(message.sentAt)}</span>
                </div>
                {renderMessageBody(message)}
              </div>
            </div>
          ))
        )}
      </div>

      <MeetChatImageLightbox imageSrc={fullscreenImageSrc} onClose={() => setFullscreenImageSrc(null)} />
    </>
  );
};

export const MeetChatMessageList = memo(MeetChatMessageListComponent);
